// src/data/books/blockchain/fundamentos/blockchain_fundamentos_ch03.js
const BLOCKCHAIN_FUNDAMENTOS_CH03 = {
  id: "blockchain-fundamentos-3",
  slug: "que-es-blockchain",
  contentId: 3,
  title: "¿Qué es Blockchain?",
  sections: [
    {
      id: "hero",
      heading: "¿Qué es Blockchain?",
      text: `Blockchain es una tecnología de registro distribuido y descentralizado que permite almacenar información de manera segura, transparente e inmutable. Funciona bajo un modelo P2P (peer-to-peer) en el que múltiples nodos mantienen una copia sincronizada del historial de transacciones, eliminando la necesidad de una autoridad central.

La validez de las operaciones se garantiza mediante mecanismos de consenso que verifican y confirman los datos  de forma colaborativa. Una vez que un bloque ha sido validado y añadido a la cadena, su contenido no puede  editarse ni eliminarse; cualquier cambio requiere la creación de una nueva transacción que se enlaza criptográficamente al registro anterior, preservando así la integridad y la trazabilidad del sistema.`
    }
    ,
    {
      id: "hero",
      heading: "Los Pilares Fundamentales de Blockchain",
      text: `La tecnología Blockchain se sostiene sobre cuatro pilares esenciales que garantizan su funcionamiento:la descentralización, la transparencia, la seguridad y la inmutabilidad. Estos principios actúan como la base que permite crear sistemas confiables sin depender de una autoridad central.

La descentralización asegura que la información no esté controlada por un solo ente, sino distribuida en múltiples nodos.
La transparencia permite que todas las transacciones sean visibles y verificables públicamente.
La seguridad se apoya en la criptografía para proteger los datos y las identidades.Y la inmutabilidad garantiza que una vez registrados, los bloques de información no puedan alterarse sin consenso de la red.`,

      image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/cap1_0.png",
        alt: "Pilares fundamentales de Blockchain: descentralización, transparencia, seguridad e inmutabilidad",
        caption: "Los pilares de Blockchain sostienen su fiabilidad y valor: descentralización, transparencia, seguridad e inmutabilidad."
      }
    },
    {
  id: "pilares-blockchain",
  heading: "Estructura  de Blockchain",
  text: `La tecnología Blockchain se compone de varios pilares que actúan como base para su funcionamiento. Cada elemento cumple un papel específico dentro del ecosistema, permitiendo que la red sea segura, transparente y descentralizada.

Los bloques almacenan las transacciones validadas, enlazadas criptográficamente para formar una cadena continua. La innovación impulsa la evolución del sistema, promoviendo nuevas aplicaciones e infraestructuras. La prueba de participación (Proof of Stake) y otros mecanismos de consenso determinan cómo se validan las operaciones, mientras que los protocolos definen las reglas que protegen la red y sus nodos.

La autonomía permite el desarrollo de organizaciones descentralizadas que operan sin intermediarios, y las aplicaciones blockchain extienden esta tecnología a distintos sectores: finanzas, logística, educación, salud y gestión pública. En conjunto, estos pilares conforman un modelo tecnológico confiable que transforma la forma en que se almacena, verifica y comparte la información a nivel global.`,
  image: {
    src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/cap1_1.png",
    alt: "Pilares y estructura de la tecnología Blockchain",
    caption: "Bloques, innovación, consenso, protocolos, autonomía y aplicaciones: los pilares que dan forma a la tecnología Blockchain."
  }
},



    /* 1) PROCESO GENERAL: va inmediatamente después de la definición
          para que el lector visualice, de punta a punta, qué ocurre 
          cuando alguien envía una transacción. */
    {
      id: "proceso-transaccion",
      heading: "¿Cómo funciona una transacción en blockchain ?",
      text: `Antes de entrar a los detalles de la estructura, conviene ver el flujo completo:
1) Un usuario solicita una transacción (por ejemplo, enviar un valor).
2) Esa petición se agrupa en un bloque candidato.
3) El bloque se envía a los nodos de la red.
4) Los nodos validan la transacción conforme a las reglas del protocolo.
5) El bloque válido se añade a la cadena existente, enlazado por su hash.
6) La transacción queda confirmada. 

Esta secuencia explica por qué hablamos de un sistema sin intermediarios y por qué la red puede ponerse de acuerdo sin una autoridad central.`,
      image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/img4.png",
        alt: "Esquema de inmutabilidad",
        caption: "Los cambios se añaden mediante nuevas transacciones"
      },
    },


    /* 2) ESTRUCTURA POR BLOQUES: ahora que el lector vio el flujo,
          profundizamos en QUÉ es un bloque y CÓMO se encadenan. */
    {
      id: "estructura-por-bloques",
      heading: "Estructura por bloques",
      text: `La cadena está compuesta por bloques que agrupan transacciones. 
Cada bloque incluye: 
(a) el hash del bloque anterior (enlace), 
(b) su propio hash (identificador), 
(c) un sello de tiempo, (d) un nonce u otros campos del consenso, 
(e) el conjunto de transacciones. 

El orden es cronológico y cada bloque está firmado/validado según el mecanismo de consenso de la red.`,
image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/ChatGPT%20Image%2011%20nov%202025%2C%2023_53_07.png",
        alt: "Línea de tiempo de las monedas digitales",
        caption: "Línea de tiempo con hitos de dinero electrónico y criptomonedas: ecash (1983), Mondex y E-gold (1996), Bitcoin (2008), Litecoin (2010), Ripple (2013), Apple Pay (2015), Tether (2017), Bitcoin Cash y Coincheck (2018), Bakkt (2020) y Mastercard (2021), entre otros.",
 
      },
    },
    

    /* 3) INMUTABILIDAD: con el concepto de ‘enlace por hash’ ya presentado,
          mostramos la lógica de “no se edita el pasado; se registra una actualización”. */
    {
      id: "inmutabilidad",
      heading: "Inmutabilidad de la cadena",
      text: `La inmutabilidad surge del encadenamiento por hash: modificar un dato antiguo 
cambiaría su hash, rompería el enlace con el bloque siguiente y haría detectable la alteración. 
Por eso, si se necesita actualizar información, no se edita el bloque histórico; 
se emite una nueva transacción que deja constancia de la corrección o el estado actual.`,
image: {
         src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/img3.png",
        alt: "Tres bloques encadenados mostrando Previous Hash",
        caption: "Inmutabilidad: si cambias un bloque, cambias su hash y se rompe el encadenamiento; por eso las ‘correcciones’ se registran como nuevas transacciones.",
 
      },
    },
   
  ]
};

export default BLOCKCHAIN_FUNDAMENTOS_CH03;
